// src/pages/SportDetail.tsx
import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import { PageHero } from '@/components/PageHero';
import { Button } from '@/components/ui/button';
import { sports } from '@/data/mock/sports';
import { branches } from '@/data/mock/branches';
import { 
  Users, 
  Calendar, 
  Trophy,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  Clock,
  Target,
  Award,
  Zap,
  Heart,
  Shield,
  TrendingUp,
  Star,
  MapPin,
  Dumbbell,
  UserCheck,
  Flame,
  Medal,
  Activity,
  BarChart
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useState } from 'react';

const SportDetail = () => {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [selectedImage, setSelectedImage] = useState(0);

  const sport = sports.find(s => s.slug === slug);

  if (!sport) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-background to-surface-1">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md"
        >
          <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-muted mb-6">
            <Trophy className="h-12 w-12 text-muted-foreground" />
          </div>
          <h1 className="text-3xl font-bold mb-4">{t('notFound.title')}</h1>
          <p className="text-muted-foreground mb-6">
            {isRTL ? 'الرياضة غير موجودة' : 'Sport not found'}
          </p>
          <Link to="/sports">
            <Button size="lg" className="btn-glow">
              <ArrowLeft className={`h-5 w-5 ${isRTL ? 'rotate-180 ml-2' : 'mr-2'}`} />
              {t('common.back')}
            </Button>
          </Link>
        </motion.div>
      </div>
    );
  }

  // Mock images gallery
  const galleryImages = [
    sport.image,
    sport.image,
    sport.image,
    sport.image,
  ];

  // Benefits of the sport
  const benefits = [
    { 
      icon: Heart, 
      title: { en: 'Physical Fitness', ar: 'اللياقة البدنية' },
      desc: { en: 'Improve cardiovascular health', ar: 'تحسين صحة القلب' }
    },
    { 
      icon: Target, 
      title: { en: 'Discipline', ar: 'الانضباط' },
      desc: { en: 'Build strong mental discipline', ar: 'بناء انضباط عقلي قوي' }
    },
    { 
      icon: Users, 
      title: { en: 'Teamwork', ar: 'العمل الجماعي' },
      desc: { en: 'Learn to work with others', ar: 'تعلم العمل مع الآخرين' }
    },
    { 
      icon: Shield, 
      title: { en: 'Confidence', ar: 'الثقة بالنفس' },
      desc: { en: 'Build self-confidence', ar: 'بناء الثقة بالنفس' }
    },
  ];

  // Training program levels
  const trainingLevels = [
    {
      level: { en: 'Beginner', ar: 'مبتدئ' },
      icon: UserCheck,
      features: [
        { en: 'Basic skills training', ar: 'تدريب على المهارات الأساسية' },
        { en: 'Individual attention', ar: 'اهتمام فردي' },
        { en: '2-3 sessions per week', ar: '2-3 جلسات في الأسبوع' },
      ]
    },
    {
      level: { en: 'Intermediate', ar: 'متوسط' },
      icon: Activity,
      features: [
        { en: 'Advanced techniques', ar: 'تقنيات متقدمة' },
        { en: 'Team practice', ar: 'ممارسة جماعية' },
        { en: '3-4 sessions per week', ar: '3-4 جلسات في الأسبوع' },
      ]
    },
    {
      level: { en: 'Advanced', ar: 'محترف' },
      icon: Medal,
      features: [
        { en: 'Competition preparation', ar: 'إعداد للمنافسات' },
        { en: 'Professional coaching', ar: 'تدريب احترافي' },
        { en: '5-6 sessions per week', ar: '5-6 جلسات في الأسبوع' },
      ]
    },
  ];

  // Available branches
  const availableBranches = branches.filter(b => 
    b.availableSports.includes(sport.id)
  );

  return (
    <div className="min-h-screen">
      {/* Enhanced Page Hero */}
      <PageHero
        title={sport.name[isRTL ? 'ar' : 'en']}
        subtitle={sport.description[isRTL ? 'ar' : 'en']}
        breadcrumbs={[
          { label: t('common.home'), path: '/' },
          { label: t('nav.sports'), path: '/sports' },
          { label: sport.name[isRTL ? 'ar' : 'en'] }
        ]}
        backgroundImage={sport.image}
      />

      {/* Back Button */}
      <section className="py-8 bg-background border-b border-border">
        <div className="container mx-auto px-4">
          <Link to="/sports">
            <Button variant="ghost" size="lg" className="group">
              <ArrowLeft className={`h-5 w-5 ${isRTL ? 'rotate-180 ml-2' : 'mr-2'} transition-transform group-hover:-translate-x-1`} />
              {isRTL ? 'العودة إلى الرياضات' : 'Back to Sports'}
            </Button>
          </Link>
        </div>
      </section>

      {/* Image Gallery Section */}
      <section className="py-12 bg-gradient-to-b from-surface-1 to-background">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-6xl mx-auto"
          >
            {/* Main Image */}
            <div className="relative h-[500px] rounded-3xl overflow-hidden mb-6 border-2 border-border shadow-2xl">
              <motion.img
                key={selectedImage}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                src={galleryImages[selectedImage]}
                alt={sport.name[isRTL ? 'ar' : 'en']}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background/60 to-transparent" />
              
              {/* Sport Badge */}
              <div className="absolute top-6 left-6 px-6 py-3 rounded-full bg-primary/95 backdrop-blur-xl border border-primary/40 shadow-2xl">
                <div className="flex items-center gap-2">
                  <Trophy className="h-5 w-5 text-primary-foreground" />
                  <span className="text-sm font-bold text-primary-foreground">
                    {sport.name[isRTL ? 'ar' : 'en']}
                  </span>
                </div>
              </div>

              {/* Image Counter */}
              <div className="absolute top-6 right-6 px-4 py-2 rounded-full bg-card/90 backdrop-blur-xl border border-border/50 shadow-lg">
                <span className="text-sm font-semibold">
                  {selectedImage + 1} / {galleryImages.length}
                </span>
              </div>
            </div>

            {/* Thumbnail Grid */}
            <div className="grid grid-cols-4 gap-4">
              {galleryImages.map((img, idx) => (
                <motion.button
                  key={idx}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setSelectedImage(idx)}
                  className={`relative h-24 rounded-xl overflow-hidden border-2 transition-all duration-300 ${
                    selectedImage === idx 
                      ? 'border-primary shadow-lg shadow-primary/20' 
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                  {selectedImage === idx && (
                    <div className="absolute inset-0 bg-primary/20 backdrop-blur-[1px]" />
                  )}
                </motion.button>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Quick Stats Section */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Coaches */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0 }}
              whileHover={{ y: -8 }}
              className="p-8 rounded-2xl bg-card border border-border hover:border-primary/50 shadow-xl hover:shadow-2xl transition-all duration-500 text-center"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 border border-primary/20 mb-4">
                <Users className="h-8 w-8 text-primary" />
              </div>
              <div className="text-5xl font-bold gradient-text mb-2">{sport.coaches}</div>
              <div className="text-sm text-muted-foreground font-medium">
                {isRTL ? 'مدرب محترف' : 'Expert Coaches'}
              </div>
            </motion.div>

            {/* Schedule */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              whileHover={{ y: -8 }}
              className="p-8 rounded-2xl bg-card border border-border hover:border-primary/50 shadow-xl hover:shadow-2xl transition-all duration-500 text-center"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 border border-primary/20 mb-4">
                <Calendar className="h-8 w-8 text-primary" />
              </div>
              <div className="text-lg font-bold mb-2">{sport.schedule[isRTL ? 'ar' : 'en']}</div>
              <div className="text-sm text-muted-foreground font-medium">
                {isRTL ? 'جدول التدريب' : 'Training Schedule'}
              </div>
            </motion.div>

            {/* Age Groups */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              whileHover={{ y: -8 }}
              className="p-8 rounded-2xl bg-card border border-border hover:border-primary/50 shadow-xl hover:shadow-2xl transition-all duration-500 text-center"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 border border-primary/20 mb-4">
                <Trophy className="h-8 w-8 text-primary" />
              </div>
              <div className="text-5xl font-bold gradient-text mb-2">{sport.ageGroups.length}</div>
              <div className="text-sm text-muted-foreground font-medium">
                {isRTL ? 'مجموعة عمرية' : 'Age Groups'}
              </div>
            </motion.div>

            {/* Gender */}
            {sport.gender && (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                whileHover={{ y: -8 }}
                className="p-8 rounded-2xl bg-card border border-border hover:border-primary/50 shadow-xl hover:shadow-2xl transition-all duration-500 text-center"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 border border-primary/20 mb-4">
                  <Users className="h-8 w-8 text-primary" />
                </div>
                <div className="text-lg font-bold mb-2">{sport.gender[isRTL ? 'ar' : 'en']}</div>
                <div className="text-sm text-muted-foreground font-medium">
                  {isRTL ? 'الفئة المستهدفة' : 'Target Group'}
                </div>
              </motion.div>
            )}

            {/* Branches */}
            {sport.branches && (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 }}
                whileHover={{ y: -8 }}
                className="p-8 rounded-2xl bg-card border border-border hover:border-primary/50 shadow-xl hover:shadow-2xl transition-all duration-500 text-center"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 border border-primary/20 mb-4">
                  <MapPin className="h-8 w-8 text-primary" />
                </div>
                <div className="text-lg font-bold mb-2">
                  {sport.branches.map(b => b[isRTL ? 'ar' : 'en']).join(' • ')}
                </div>
                <div className="text-sm text-muted-foreground font-medium">
                  {isRTL ? 'الفروع المتاحة' : 'Available Branches'}
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gradient-to-b from-surface-1 to-background relative overflow-hidden">
        {/* Background Decoration */}
        <div className="absolute inset-0 bg-grid-pattern opacity-5" />

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary/10 border border-primary/20 mb-8">
              <Zap className="h-5 w-5 text-primary" />
              <span className="text-sm font-semibold text-primary">
                {isRTL ? 'الفوائد' : 'Benefits'}
              </span>
            </div>

            <h2 className="text-4xl sm:text-5xl font-bold mb-6 gradient-text">
              {isRTL ? 'لماذا تختار هذه الرياضة؟' : 'Why Choose This Sport?'}
            </h2>

            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              {isRTL
                ? 'اكتشف الفوائد العديدة التي ستحصل عليها من ممارسة هذه الرياضة'
                : 'Discover the many benefits you will gain from practicing this sport'
              }
            </p>
          </motion.div>

          {/* Benefits Grid */}
          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                whileHover={{ y: -8 }}
                className="p-8 rounded-2xl bg-card border border-border hover:border-primary/50 shadow-xl hover:shadow-2xl transition-all duration-500 text-center"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 border border-primary/20 mb-4">
                  <benefit.icon className="h-8 w-8 text-primary" />
                </div>
                <h3 className="font-bold text-lg mb-2">
                  {benefit.title[isRTL ? 'ar' : 'en']}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {benefit.desc[isRTL ? 'ar' : 'en']}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Age Groups Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary/10 border border-primary/20 mb-8">
                <Users className="h-5 w-5 text-primary" />
                <span className="text-sm font-semibold text-primary">
                  {isRTL ? 'الفئات العمرية' : 'Age Groups'}
                </span>
              </div>

              <h2 className="text-4xl sm:text-5xl font-bold mb-6 gradient-text">
                {isRTL ? 'برامج لجميع الأعمار' : 'Programs for All Ages'}
              </h2>

              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                {isRTL
                  ? 'نوفر برامج تدريبية متخصصة لكل فئة عمرية'
                  : 'We provide specialized training programs for every age group'
                }
              </p>
            </motion.div>

            {/* Age Groups Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {sport.ageGroups.map((ageGroup, idx) => {
                const isObj = ageGroup !== null && typeof ageGroup === 'object' && 'range' in ageGroup;
                const range = isObj ? (ageGroup as any).range : String(ageGroup);
                const nameObj = isObj ? (ageGroup as any).name : null;
                const displayName = nameObj ? (isRTL ? nameObj.ar : nameObj.en) : null;
                return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.05 }}
                  whileHover={{ scale: 1.05, y: -4 }}
                  className="p-8 rounded-2xl bg-primary/10 border-2 border-primary/30 hover:bg-primary/20 transition-all duration-300 text-center"
                >
                  {displayName ? (
                    <div className="text-2xl font-bold text-champions-accent uppercase tracking-wide">{displayName}</div>
                  ) : (
                    <>
                      <div className="text-4xl font-bold text-primary mb-2">{range}</div>
                      <div className="text-sm text-muted-foreground font-medium">
                        {isRTL ? 'عام' : 'years'}
                      </div>
                    </>
                  )}
                </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Training Levels Section */}
      <section className="py-20 bg-gradient-to-b from-surface-1 to-background relative overflow-hidden">
        {/* Background */}
        <motion.div
          animate={{
            backgroundPosition: ["0% 50%", "100% 50%"],
          }}
          transition={{ duration: 20, repeat: Infinity, repeatType: "reverse" }}
          className="absolute inset-0 bg-gradient-to-r from-primary/5 via-accent/5 to-primary/5 bg-[length:200%_100%]"
        />

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary/10 border border-primary/20 mb-8">
              <TrendingUp className="h-5 w-5 text-primary" />
              <span className="text-sm font-semibold text-primary">
                {isRTL ? 'مستويات التدريب' : 'Training Levels'}
              </span>
            </div>

            <h2 className="text-4xl sm:text-5xl font-bold mb-6 gradient-text">
              {isRTL ? 'اختر المستوى المناسب لك' : 'Choose Your Level'}
            </h2>

            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              {isRTL
                ? 'برامج تدريبية متدرجة تناسب جميع المستويات من المبتدئ إلى المحترف'
                : 'Progressive training programs suitable for all levels from beginner to professional'
              }
            </p>
          </motion.div>

          {/* Training Levels Grid */}
          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
            {trainingLevels.map((level, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                whileHover={{ y: -8 }}
                className="relative p-8 rounded-3xl bg-card border-2 border-border hover:border-primary/50 shadow-xl hover:shadow-2xl hover:shadow-primary/10 transition-all duration-500"
              >
                {/* Icon */}
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 border border-primary/20 mb-6">
                  <level.icon className="h-8 w-8 text-primary" />
                </div>

                {/* Title */}
                <h3 className="text-2xl font-bold mb-6">
                  {level.level[isRTL ? 'ar' : 'en']}
                </h3>

                {/* Features */}
                <ul className="space-y-4">
                  {level.features.map((feature, fIdx) => (
                    <li key={fIdx} className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-muted-foreground">
                        {feature[isRTL ? 'ar' : 'en']}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <Button className="w-full mt-8" variant="outline">
                  {isRTL ? 'انضم الآن' : 'Join Now'}
                </Button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Available Branches Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary/10 border border-primary/20 mb-8">
                <MapPin className="h-5 w-5 text-primary" />
                <span className="text-sm font-semibold text-primary">
                  {isRTL ? 'الفروع المتاحة' : 'Available Branches'}
                </span>
              </div>

              <h2 className="text-4xl sm:text-5xl font-bold mb-6 gradient-text">
                {isRTL ? 'متاح في {count} فرع'.replace('{count}', availableBranches.length.toString()) : `Available in ${availableBranches.length} Branches`}
              </h2>

              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                {isRTL
                  ? 'اختر الفرع الأقرب إليك وابدأ رحلتك الرياضية'
                  : 'Choose the nearest branch and start your sports journey'
                }
              </p>
            </motion.div>

            {/* Branches Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {availableBranches.map((branch, idx) => (
                <motion.div
                  key={branch.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  whileHover={{ y: -4 }}
                >
                  <Link to={`/branches/${branch.slug}`} className="group block">
                    <div className="p-6 rounded-2xl bg-card border border-border hover:border-primary/50 shadow-xl hover:shadow-2xl transition-all duration-500">
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center">
                          <MapPin className="h-6 w-6 text-primary" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-bold mb-2 group-hover:text-primary transition-colors">
                            {branch.name[isRTL ? 'ar' : 'en']}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            {branch.address[isRTL ? 'ar' : 'en']}
                          </p>
                        </div>
                        <ArrowRight className={`h-5 w-5 text-primary flex-shrink-0 ${isRTL ? 'rotate-180' : ''}`} />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-b from-surface-1 to-background relative overflow-hidden">
        {/* Animated Background */}
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-radial from-primary/20 via-accent/10 to-transparent rounded-full blur-3xl"
        />

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary/10 border border-primary/20 mb-8">
              <Flame className="h-5 w-5 text-primary" />
              <span className="text-sm font-semibold text-primary">
                {isRTL ? 'ابدأ الآن' : 'Start Now'}
              </span>
            </div>

            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6 gradient-text">
              {isRTL ? 'جاهز للانضمام؟' : 'Ready to Join?'}
            </h2>

            <p className="text-xl text-muted-foreground mb-10 max-w-2xl mx-auto">
              {isRTL
                ? 'احجز جلستك التجريبية المجانية اليوم وابدأ رحلتك في {sport}'.replace('{sport}', sport.name.ar)
                : `Book your free trial session today and start your ${sport.name.en} journey`
              }
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/booking">
                <Button size="lg" className="btn-glow px-12 py-7 text-lg rounded-full">
                  <Calendar className={`h-5 w-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                  {isRTL ? 'احجز الآن' : 'Book Now'}
                </Button>
              </Link>

              <Button size="lg" variant="outline" className="px-12 py-7 text-lg rounded-full border-2">
                <Users className={`h-5 w-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                {isRTL ? 'تواصل معنا' : 'Contact Us'}
              </Button>
            </div>

            {/* Trust Indicators */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="flex flex-wrap justify-center gap-6 mt-12 pt-12 border-t border-border"
            >
              {[
                { icon: CheckCircle2, text: isRTL ? 'جلسة تجريبية مجانية' : 'Free Trial Session' },
                { icon: Shield, text: isRTL ? 'بيئة آمنة' : 'Safe Environment' },
                { icon: Award, text: isRTL ? 'مدربون معتمدون' : 'Certified Coaches' },
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-2 text-muted-foreground">
                  <item.icon className="h-5 w-5 text-primary" />
                  <span className="text-sm font-medium">{item.text}</span>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default SportDetail;
